import { Button } from "@choose/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@choose/ui/empty";
import { Separator } from "@choose/ui/separator";
import { Spinner } from "@choose/ui/spinner";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeftIcon, CandyOffIcon, ServerCrashIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { SnippetTagForm } from "~/components/common/settings/snippets/snippet-tag-form";
import { useSnippetTagQuery } from "~/graphql/graphql.gen";

export const Route = createFileRoute(
  "/_auth/_app_shell/_settings-navigation/settings/snippet-tag/$snippetTagId",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const { snippetTagId } = Route.useParams();

  const { data, error, isLoading } = useSnippetTagQuery(
    {
      supportSnippetTagId: snippetTagId,
    },
    {
      select: (data) => data.supportSnippetTag,
    },
  );

  if (isLoading) {
    return (
      <>
        <title>{t("app_name")}</title>

        <div className="bg-background flex size-full flex-col items-center justify-center overflow-auto">
          <Empty className="min-h-[300px] w-full max-w-md">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Spinner className="size-4" />
              </EmptyMedia>
              <EmptyTitle>{t("snippet_tag.loading.title")}</EmptyTitle>
              <EmptyDescription>
                {t("snippet_tag.loading.description")}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        </div>
      </>
    );
  }

  if (
    !data ||
    (error as Error | undefined)?.message === "snippet_tag_not_found"
  )
    return (
      <>
        <title>{`${t("snippet_tag.not_found.title")} - ${t("app_name")}`}</title>

        <div className="bg-background flex size-full overflow-auto">
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <CandyOffIcon />
              </EmptyMedia>
              <EmptyTitle>{t("snippet_tag.not_found.title")}</EmptyTitle>
              <EmptyDescription>
                {t("snippet_tag.not_found.description")}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        </div>
      </>
    );

  if (error) {
    return (
      <>
        <title>{t("app_name")}</title>

        <div className="bg-background flex size-full overflow-auto">
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <ServerCrashIcon />
              </EmptyMedia>
              <EmptyTitle>{t("snippet_tag.error")}</EmptyTitle>
            </EmptyHeader>
          </Empty>
        </div>
      </>
    );
  }

  return (
    <>
      <title>{`${t("snippet_tag.edit_x", { name: data.label })} - ${t("app_name")}`}</title>

      <div className="bg-background w-full overflow-auto">
        <div className="m-auto max-w-2xl p-8">
          <header className="mt-14">
            <Button
              variant="ghost"
              size="sm"
              className="-mx-3 mb-4"
              nativeButton={false}
              render={
                <Link to="/settings/snippet-tags">
                  <ArrowLeftIcon />
                  <span>{t("settings.snippet_tags.all_snippet_tags")}</span>
                </Link>
              }
            />

            <h1 className="text-2xl">
              {t("settings.snippet_tags.add_snippet_tag")}
            </h1>
          </header>
          <Separator className="my-6" />
          <SnippetTagForm snippetTag={data} />
        </div>
      </div>
    </>
  );
}
